// src/pages/Dashboard/Establecimiento/ConfigurarDias.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, message, Typography, Button, InputNumber, Spin, Row, Col, Divider, Alert } from 'antd';
import { ClockCircleOutlined, SaveOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import apiClient from '../../../api/apiClient';

const { Title, Text } = Typography;

const CLAVES = {
  ZONA: 'DIAS_NOTIFICACION_TAREA_SIN_CALIFICAR',
  FINAL: 'DIAS_NOTIFICACION_EXAMEN_FINAL_SIN_CALIFICAR',
};

const ConfigurarDias = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(null); // clave que se está guardando
  const [configuraciones, setConfiguraciones] = useState({});

  useEffect(() => {
    cargarConfiguraciones();
  }, []);

  const cargarConfiguraciones = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/configuraciones');
      if (res.data.success && res.data.data) {
        const mapa = {};
        res.data.data.forEach(c => {
          mapa[c.Clave] = { ...c, ValorEditable: parseInt(c.Valor, 10) };
        });
        setConfiguraciones(mapa);
      }
    } catch (err) {
      message.error('Error al cargar las configuraciones');
    } finally {
      setLoading(false);
    }
  };

  const handleGuardar = async (clave) => {
    const config = configuraciones[clave];
    if (!config) return;

    const valor = config.ValorEditable;
    if (!valor || valor < 1) {
      message.warning('El valor debe ser al menos 1 día');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setGuardando(clave);
    try {
      await apiClient.put(`/configuraciones/${clave}`, {
        Valor: String(valor),
        ModificadoPor: user.IdUsuario,
      });
      message.success('Configuración guardada correctamente');
      await cargarConfiguraciones();
    } catch (err) {
      message.error('Error al guardar la configuración');
    } finally {
      setGuardando(null);
    }
  };

  const handleCambio = (clave, valor) => {
    setConfiguraciones(prev => ({
      ...prev,
      [clave]: { ...prev[clave], ValorEditable: valor },
    }));
  };

  const items = [
    {
      clave: CLAVES.ZONA,
      titulo: 'Actividades de Zona',
      descripcion: 'Días que pueden pasar sin que el docente califique una actividad de zona antes de recibir una notificación.',
      color: '#4ecdc4',
      icono: '📝',
    },
    {
      clave: CLAVES.FINAL,
      titulo: 'Exámenes Finales',
      descripcion: 'Días que pueden pasar sin que el docente califique un examen final antes de recibir una notificación.',
      color: '#f7b731',
      icono: '📋',
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Cargando configuraciones...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>
        <ClockCircleOutlined /> Configurar Días de Notificación
      </Title>
      <Title level={5} type="secondary">
        Días límite antes de notificar a los docentes sobre actividades sin calificar
      </Title>

      <Alert
        type="info"
        icon={<InfoCircleOutlined />}
        showIcon
        style={{ marginTop: 16, marginBottom: 24 }}
        message="¿Cómo funciona?"
        description="Cuando un docente tiene una actividad sin calificar y han pasado más días de los configurados aquí, el sistema enviará una notificación automática a ese docente."
      />

      <Row gutter={[24, 24]}>
        {items.map(({ clave, titulo, descripcion, color, icono }) => {
          const config = configuraciones[clave];
          return (
            <Col xs={24} md={12} key={clave}>
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>{icono}</span>
                    <span style={{ fontSize: 16, fontWeight: 'bold' }}>{titulo}</span>
                  </div>
                }
                style={{
                  borderTop: `4px solid ${color}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
                  {descripcion}
                </Text>

                <div style={{ marginBottom: 24 }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Días límite
                  </Text>
                  <InputNumber
                    value={config?.ValorEditable ?? ''}
                    onChange={(val) => handleCambio(clave, val)}
                    min={1}
                    max={365}
                    style={{ width: '100%' }}
                    size="large"
                    addonAfter="días"
                    disabled={!config}
                  />
                </div>

                {config?.FechaModificado && (
                  <div style={{ marginBottom: 16, padding: 10, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Última modificación: {new Date(config.FechaModificado).toLocaleString('es-GT')}
                    </Text>
                  </div>
                )}

                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => handleGuardar(clave)}
                  loading={guardando === clave}
                  disabled={!config}
                  block
                  size="large"
                  style={{ backgroundColor: color, borderColor: color }}
                >
                  Guardar
                </Button>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Divider />

      <div style={{ textAlign: 'center', marginTop: 8 }}>
        <Button icon={<ReloadOutlined />} onClick={cargarConfiguraciones} loading={loading} size="large" style={{ marginRight: 16 }}>
          Recargar
        </Button>
        <Button size="large" onClick={() => navigate('/dashboard')}>
          Regresar al Dashboard
        </Button>
      </div>
    </div>
  );
};

export default ConfigurarDias;
